import React from "react";
import { useSelector } from "react-redux";
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AnalyticsResourceType } from "@intelligems/ig-types";
import { ensureNumber } from "@intelligems/ig-utils";

import { useGraphValueFormatter } from "../../../api/formatters/dashboard";
import { selectedVariations } from "../../../store/slices/variations";
import theme from "../../../theme";
import type { LineMetricData } from "../../../types/analytics";
import { ExperienceFe } from "../../../types/schemas/experiences";

import type { GraphConfig } from "./Utils/graphConfigs";
import { lineGroupColors } from "./Utils/graphHelpers";
import {
  CustomizedAxisTick,
  CustomResponsiveContainer,
  StyledLegendText,
} from "./Utils/graphStyleCustoms";

interface LineGraphProps {
  config: GraphConfig;
  groupMetricData: LineMetricData[];
  resource?: ExperienceFe;
  resourceType: AnalyticsResourceType;
  filterMethod?: string;
  metricFilterIndex?: number;
}

export const LineGraph = ({
  config,
  groupMetricData,
  resource,
  resourceType,
  filterMethod,
  metricFilterIndex,
}: LineGraphProps) => {
  const graphValueFormatter = useGraphValueFormatter();
  const dataMin = config.isLossMetric ? 0 : (config.yAxisDataMin ?? "dataMin");
  const variations = useSelector(selectedVariations);

  return (
    <CustomResponsiveContainer width="100%" height={300}>
      <LineChart
        data={groupMetricData}
        style={{ marginLeft: -24 }}
        margin={{
          top: 5,
          bottom: 5,
        }}
      >
        <CartesianGrid
          style={{ stroke: theme.palette.disabled.light }}
          vertical={false}
        />
        <XAxis
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 14,
              }}
              value={
                config.xAxisLabel && config.xAxisLabel[metricFilterIndex || 0]
              }
              position={"insideBottom"}
              offset={-5}
              textAnchor={"middle"}
            />
          }
          padding={{ left: 30, right: 30 }}
          tick={(props: any) => {
            return (
              // @ts-ignore
              <CustomizedAxisTick
                props={props}
                groupMetricData={groupMetricData}
                graphValueFormatter={graphValueFormatter}
                config={config}
              />
            );
          }}
          axisLine={{ stroke: theme.palette.disabled.light }}
          tickLine={false}
          dataKey={"xAxisKey"}
        />
        <YAxis
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 14,
                marginTop: "10px",
                textAnchor: "middle",
              }}
              angle={270}
              value={
                config.yAxisLabel && config.yAxisLabel[metricFilterIndex || 0]
              }
              position={"insideLeft"}
              offset={10}
              textAnchor={"middle"}
            />
          }
          domain={[dataMin, "dataMax"]}
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 14 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: any) =>
            graphValueFormatter(true, value, config)!
          }
        />
        {config.formatTooltipLabel ? (
          <Tooltip
            label={"name"}
            labelFormatter={(label: any) => {
              return graphValueFormatter(false, ensureNumber(label), config);
            }}
            contentStyle={{
              fontFamily: theme.typography.fontFamily,
              borderRadius: 5,
              fontSize: 14,
              backgroundColor: "rgba(97, 97, 97, 0.92)",
              color: "#FFF",
              border: "1px solid transparent",
            }}
            itemStyle={{ color: "#FFF" }}
            cursor={{ fill: "transparent" }}
            formatter={(value: any, name: any) => {
              if (config.legendFormatFunction) {
                return [
                  graphValueFormatter(false, value, config)!,
                  config.legendFormatFunction(
                    variations.find((v) => v.id === name)?.name
                  ),
                ];
              } else {
                return [
                  graphValueFormatter(false, value, config)!,
                  variations.find((v) => v.id === name)?.name,
                ];
              }
            }}
          />
        ) : (
          <Tooltip
            label={"name"}
            contentStyle={{
              fontFamily: theme.typography.fontFamily,
              borderRadius: 5,
              fontSize: 14,
              backgroundColor: "rgba(97, 97, 97, 0.92)",
              color: "#FFF",
              border: "1px solid transparent",
            }}
            itemStyle={{ color: "#FFF" }}
            cursor={{ fill: "transparent" }}
            formatter={(value: any, name: any) => {
              if (config.legendFormatFunction) {
                return [
                  graphValueFormatter(false, value, config)!,
                  config.legendFormatFunction(
                    variations.find((v) => v.id === name)?.name
                  ),
                ];
              } else {
                return [
                  graphValueFormatter(false, value, config)!,
                  variations.find((v) => v.id === name)?.name,
                ];
              }
            }}
          />
        )}
        {config.showLegend && (
          <Legend
            wrapperStyle={{
              paddingLeft: "25px",
              paddingTop: "10px",
            }}
            align={"center"}
            formatter={(value, entry, index) => {
              return StyledLegendText(
                variations.find((v) => v.id === value)?.name || "",
                entry,
                index,
                config?.legendFormatFunction
              );
            }}
            iconType={"circle"}
            iconSize={9}
          />
        )}
        {config.isLossMetric && filterMethod ? (
          <Line
            strokeWidth={1.5}
            dot={false}
            key={filterMethod}
            type="linear"
            dataKey={filterMethod}
            stroke={lineGroupColors[0]}
          />
        ) : resourceType === "experiences" && resource ? (
          variations.map((group, index) => (
            <Line
              strokeWidth={1.5}
              dot={false}
              key={group.id}
              type="linear"
              dataKey={group.id}
              stroke={lineGroupColors[index]}
            />
          ))
        ) : config.metrics.length > 1 ? (
          config.metrics.map((metric, index) => (
            <Line
              strokeWidth={1.5}
              dot={false}
              key={metric}
              type="linear"
              dataKey={metric}
              stroke={lineGroupColors[index]}
            />
          ))
        ) : (
          <Line
            type="linear"
            dataKey={"value"}
            strokeWidth={1.5}
            stroke={lineGroupColors[0]}
          />
        )}
      </LineChart>
    </CustomResponsiveContainer>
  );
};
